'use client';
import React, { useState } from 'react';
import { useRouter } from 'next/navigation';
import Link from 'next/link';
import Icon from '@/components/ui/AppIcon';
import { supabase } from '@/lib/supabase';

interface FormData {
  fullName: string;
  email: string;
  phone: string;
  password: string;
  confirmPassword: string;
}

export default function CustomerSignUpForm() {
  const router = useRouter();
  const [form, setForm] = useState<FormData>({ fullName: '', email: '', phone: '', password: '', confirmPassword: '' });
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isGoogleLoading, setIsGoogleLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [registeredEmail, setRegisteredEmail] = useState('');

  const handleGoogleSignIn = async () => {
    setIsGoogleLoading(true);
    setError('');
    try {
      const origin = typeof window !== 'undefined'
        ? window.location.origin
        : (process.env.NEXT_PUBLIC_SITE_URL || 'https://bozaat5282.builtwithrocket.new');
      const { error: oauthError } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: { redirectTo: `${origin}/auth/callback?next=/customer-dashboard` },
      });
      if (oauthError) {
        setError(oauthError.message || 'Google sign-in failed.');
        setIsGoogleLoading(false);
      }
    } catch {
      setError('Something went wrong. Please try again.');
      setIsGoogleLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (!form.fullName || !form.email || !form.password) {
      setError('Please fill in all required fields.');
      return;
    }
    if (!form.phone || form.phone.trim() === '') {
      setError('Phone number is required.');
      return;
    }
    if (form.password.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }
    if (form.password !== form.confirmPassword) {
      setError('Passwords do not match.');
      return;
    }
    setIsLoading(true);
    try {
      const siteUrl = typeof window !== 'undefined'
        ? window.location.origin
        : (process.env.NEXT_PUBLIC_SITE_URL || 'https://bozaat5282.builtwithrocket.new');
      const { data: authData, error: authError } = await supabase.auth.signUp({
        email: form.email.trim().toLowerCase(),
        password: form.password,
        options: {
          data: { full_name: form.fullName, phone: form.phone, role: 'customer' },
          emailRedirectTo: `${siteUrl}/auth/callback?next=/customer-dashboard`,
        },
      });
      if (authError) {
        setError(authError.message || 'Failed to create account.');
        setIsLoading(false);
        return;
      }

      // Detect duplicate email (Supabase returns identities: [] silently)
      if (authData?.user && (!authData.user.identities || authData.user.identities.length === 0)) {
        setError('An account with this email already exists. Please sign in instead.');
        setIsLoading(false);
        return;
      }

      const userId = authData.user?.id;
      if (!userId) {
        setError('Account creation failed. Please try again.');
        setIsLoading(false);
        return;
      }

      // The trigger handle_new_user_profile() creates the profile automatically.
      // We attempt an upsert as a best-effort update to set customer-specific fields.
      // This is non-blocking — failure here does NOT prevent account creation.
      try {
        await supabase.from('user_profiles').upsert({
          id: userId,
          email: form.email.trim().toLowerCase(),
          full_name: form.fullName,
          phone: form.phone,
          role: 'customer',
          is_active: true,
          loyalty_points: 0,
          lifetime_points: 0,
        }, { onConflict: 'id' });
      } catch {
        // Non-fatal: trigger already created the profile
      }

      setRegisteredEmail(form.email);
      setSuccess(true);
    } catch {
      setError('Something went wrong. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  if (success) {
    return (
      <div className="bg-card border border-border rounded-2xl shadow-card p-8 text-center">
        <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-5">
          <Icon name="EnvelopeIcon" size={32} className="text-primary" />
        </div>
        <h2 className="text-xl font-700 text-foreground mb-2">Verify Your Email</h2>
        <p className="text-sm text-muted-foreground mb-1">We&apos;ve sent a confirmation link to:</p>
        <p className="text-sm font-600 text-primary mb-4 break-all">{registeredEmail}</p>
        <p className="text-sm text-muted-foreground mb-6">
          Click the link in your email to activate your account and start shopping on Bozaat.
        </p>
        <button
          onClick={() => router.push('/customer-login')}
          className="btn-primary w-full py-2.5 rounded-xl text-sm font-600"
        >
          Go to Login
        </button>
      </div>
    );
  }

  return (
    <div className="bg-card border border-border rounded-2xl shadow-card overflow-hidden">
      <div className="p-8 space-y-5">
        {/* Google */}
        <button
          type="button"
          onClick={handleGoogleSignIn}
          disabled={isGoogleLoading || isLoading}
          className="w-full flex items-center justify-center gap-3 border border-input rounded-xl px-4 py-3 text-sm font-600 text-foreground bg-card hover:bg-muted transition-colors disabled:opacity-70 disabled:cursor-not-allowed min-h-[46px]"
        >
          {isGoogleLoading ? (
            <><Icon name="ArrowPathIcon" size={16} className="animate-spin" /> Connecting…</>
          ) : (
            <>
              <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M17.64 9.2045C17.64 8.5663 17.5827 7.9527 17.4764 7.3636H9V10.845H13.8436C13.635 11.97 13.0009 12.9231 12.0477 13.5613V15.8195H14.9564C16.6582 14.2527 17.64 11.9454 17.64 9.2045Z" fill="#4285F4"/>
                <path d="M9 18C11.43 18 13.4673 17.1941 14.9564 15.8195L12.0477 13.5613C11.2418 14.1013 10.2109 14.4204 9 14.4204C6.65591 14.4204 4.67182 12.8372 3.96409 10.71H0.957275V13.0418C2.43818 15.9831 5.48182 18 9 18Z" fill="#34A853"/>
                <path d="M3.96409 10.71C3.78409 10.17 3.68182 9.5931 3.68182 9C3.68182 8.4069 3.78409 7.83 3.96409 7.29V4.9582H0.957275C0.347727 6.1731 0 7.5477 0 9C0 10.4523 0.347727 11.8269 0.957275 13.0418L3.96409 10.71Z" fill="#FBBC05"/>
                <path d="M9 3.5795C10.3214 3.5795 11.5077 4.0336 12.4405 4.9255L15.0218 2.3441C13.4632 0.8918 11.4259 0 9 0C5.48182 0 2.43818 2.0168 0.957275 4.9582L3.96409 7.29C4.67182 5.1627 6.65591 3.5795 9 3.5795Z" fill="#EA4335"/>
              </svg>
              Continue with Google
            </>
          )}
        </button>

        <div className="flex items-center gap-3">
          <div className="flex-1 h-px bg-border" />
          <span className="text-xs text-muted-foreground font-500">or sign up with email</span>
          <div className="flex-1 h-px bg-border" />
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-xl px-4 py-3 flex items-start gap-2.5">
            <Icon name="ExclamationTriangleIcon" size={16} className="text-red-500 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-red-700">{error}</p>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-600 text-foreground mb-1.5">Full Name <span className="text-red-500">*</span></label>
            <input
              type="text"
              placeholder="Rahul Sharma"
              value={form.fullName}
              onChange={e => setForm(f => ({ ...f, fullName: e.target.value }))}
              className="w-full border border-input rounded-xl px-4 py-2.5 text-sm text-foreground bg-card input-focus placeholder:text-muted-foreground"
            />
          </div>
          <div>
            <label className="block text-sm font-600 text-foreground mb-1.5">Email Address <span className="text-red-500">*</span></label>
            <input
              type="email"
              placeholder="you@example.com"
              value={form.email}
              onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
              className="w-full border border-input rounded-xl px-4 py-2.5 text-sm text-foreground bg-card input-focus placeholder:text-muted-foreground"
            />
          </div>
          <div>
            <label className="block text-sm font-600 text-foreground mb-1.5">Phone Number <span className="text-red-500">*</span></label>
            <input
              type="tel"
              placeholder="+91 98765 43210"
              value={form.phone}
              onChange={e => setForm(f => ({ ...f, phone: e.target.value }))}
              required
              className="w-full border border-input rounded-xl px-4 py-2.5 text-sm text-foreground bg-card input-focus placeholder:text-muted-foreground"
            />
          </div>
          <div>
            <label className="block text-sm font-600 text-foreground mb-1.5">Password <span className="text-red-500">*</span></label>
            <div className="relative">
              <input
                type={showPassword ? 'text' : 'password'}
                placeholder="Min. 8 characters"
                value={form.password}
                onChange={e => setForm(f => ({ ...f, password: e.target.value }))}
                className="w-full border border-input rounded-xl px-4 py-2.5 pr-11 text-sm text-foreground bg-card input-focus placeholder:text-muted-foreground"
              />
              <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
                <Icon name={showPassword ? 'EyeSlashIcon' : 'EyeIcon'} size={18} />
              </button>
            </div>
          </div>
          <div>
            <label className="block text-sm font-600 text-foreground mb-1.5">Confirm Password <span className="text-red-500">*</span></label>
            <input
              type="password"
              placeholder="Repeat your password"
              value={form.confirmPassword}
              onChange={e => setForm(f => ({ ...f, confirmPassword: e.target.value }))}
              className="w-full border border-input rounded-xl px-4 py-2.5 text-sm text-foreground bg-card input-focus placeholder:text-muted-foreground"
            />
          </div>

          <button
            type="submit"
            disabled={isLoading || isGoogleLoading}
            className="btn-primary w-full py-3 rounded-xl text-sm font-600 disabled:opacity-70 disabled:cursor-not-allowed flex items-center justify-center gap-2 min-h-[46px]"
          >
            {isLoading ? (
              <><Icon name="ArrowPathIcon" size={16} className="animate-spin" /> Creating account…</>
            ) : 'Create Account'}
          </button>
        </form>

        <p className="text-center text-xs text-muted-foreground">
          By signing up, you agree to our{' '}
          <Link href="/terms-of-service" className="text-primary hover:underline">Terms of Service</Link>
          {' '}and{' '}
          <Link href="/privacy-policy" className="text-primary hover:underline">Privacy Policy</Link>
        </p>

        <p className="text-center text-sm text-muted-foreground">
          Already have an account?{' '}
          <Link href="/customer-login" className="text-primary font-600 hover:underline">Sign in</Link>
        </p>
      </div>
    </div>
  );
}
